import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  input,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { INavigation, IUser } from '@core/models';
import { Authentication } from '@core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BottomSheet } from '../bottom-sheet/bottom-sheet.component';
import { LoginForm } from 'src/app/features/user/components/login-form/login-form.component';
import { RegisterForm } from 'src/app/features/user/components/register-form/register-form.component';

/** Un destino de la barra inferior. */
interface BottomNavTab {
  /** Ruta absoluta a la que navega. */
  readonly link: string;
  /** Texto bajo el icono. */
  readonly label: string;
  /** Clase de icono (FontAwesome o `inm-icon-*`). */
  readonly icon: string;
  /** `true` cuando la ruta solo debe marcarse activa con coincidencia exacta. */
  readonly exact: boolean;
}

/**
 * ### **MobileBottomNav**
 *
 * Barra de navegación fija al pie, visible solo en móvil. Da a la web el acceso rápido
 * de una app sin duplicar el menú: sus destinos son rutas reales del proyecto.
 *
 * La pestaña de cuenta lleva al perfil si hay sesión y, si no, abre el formulario de
 * ingreso dentro de un {@link BottomSheet}.
 */
@Component({
  selector: 'app-mobile-bottom-nav',
  imports: [RouterModule, BottomSheet, LoginForm, RegisterForm],
  templateUrl: './mobile-bottom-nav.component.html',
  styleUrl: './mobile-bottom-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileBottomNav {
  private readonly auth = inject(Authentication);
  private readonly router = inject(Router);
  private readonly modalService = inject(NgbModal);
  private readonly injector = inject(Injector);

  /**
   * Items del menú principal. La barra no los renderiza — mantiene un conjunto reducido
   * propio — pero los recibe para poder derivarlos si el menú crece.
   */
  readonly navigation = input<INavigation[]>([]);

  /** Estado del panel de cuenta. */
  protected readonly accountSheetOpen = signal<boolean>(false);

  /** Usuario autenticado, o `null` si la sesión está cerrada. */
  protected readonly user = signal<IUser | null>(null);

  /**
   * Destinos de la barra. Se limitan a lo que hoy existe en el proyecto: la maqueta
   * original traía "Favoritos", que aún no es una funcionalidad disponible.
   */
  protected readonly tabs: readonly BottomNavTab[] = [
    { link: '/', label: 'Inicio', icon: 'fa-solid fa-house', exact: true },
    { link: '/search', label: 'Explorar', icon: 'fa-solid fa-magnifying-glass', exact: false },
    { link: '/cities', label: 'Municipios', icon: 'fa-solid fa-location-dot', exact: false },
    { link: '/realidad-virtual', label: 'VR', icon: 'inm-icon-vr-glasses', exact: false },
  ];

  constructor() {
    this.readAuth();

    this.auth.onAuthenticate.pipe(takeUntilDestroyed()).subscribe(() => {
      this.readAuth();
      this.accountSheetOpen.set(false);
    });

    this.auth.onLogout.pipe(takeUntilDestroyed()).subscribe(() => this.user.set(null));
  }

  /** Va al perfil si hay sesión; si no, abre el formulario de ingreso. */
  protected openAccount(): void {
    if (this.user()) {
      this.router.navigate(['/profile']);
      return;
    }

    this.accountSheetOpen.set(true);
  }

  /** Cierra el login y abre el registro cuando el body ya recuperó su scroll. */
  protected openMobileRegister(content: TemplateRef<unknown>): void {
    this.accountSheetOpen.set(false);

    afterNextRender(
      () => {
        this.modalService.open(content, {
          fullscreen: true,
          scrollable: true,
          windowClass: 'register-mobile-modal',
        });
      },
      { injector: this.injector },
    );
  }

  private readAuth(): void {
    this.user.set(this.auth.getAuthInformation());
  }
}
